import { GraphQLError } from "graphql"
import { groq } from "next-sanity"

import { CHECKOUT_DISABLED_BRANCH_IDS } from "@/constants"
import { groqArticleProjection } from "@/graphql/schemas/shared/sanity/article.projection"
import { groqBranchOfficeProjection } from "@/graphql/schemas/shared/sanity/branchOffice.projection"
import { groqQueryOptions } from "@/graphql/schemas/shared/utils/groqQueryOptions"

/**
 * Helper method to retrieve multiple objects from sanity
 *
 * @param {{articleSkus: [string], customerBranch: string, branch: string }} queryObject
 * @param {SanityClient} sanityClient
 * @param {boolean} isRB
 *
 * @returns {Promise<[]|null>}
 */
export const getSanityOfficesAndArticles = async (queryObject, sanityClient, isRB) => {
  const { draftsFilter } = groqQueryOptions({ includeDrafts: true })
  const { articleSkus, customerBranch, branch } = queryObject

  const sanityObject = await sanityClient.fetch(
    groq`{
      "customerBranch": *[_type=="branchOffice" && id==$customerBranch ${draftsFilter}][0] {
        ...,
        ${groqBranchOfficeProjection()}
      },
      "branch": *[_type=="branchOffice" && id==$branch ${draftsFilter}][0] {
        ...,
        ${groqBranchOfficeProjection()}
      },
      "articles": *[_type=="article" && sku in $articleSkus ${draftsFilter}]{
        ...,
        ${groqArticleProjection()}
      }
    }`,
    { customerBranch: customerBranch, branch: branch, articleSkus: articleSkus },
  )

  // this unpublished article SKUs are needed for reservation
  const draftArticleSkus = isRB
    ? articleSkus?.filter((sku) => !sanityObject?.articles.some((article) => article.sku === sku))
    : []

  if (!sanityObject) return null

  return { draftArticleSkus, ...sanityObject }
}

export function noAlternativeShippingAddress(deliveryObjectValues) {
  const { name, street, postalCode, city, country } = deliveryObjectValues

  const isNotEmpty = (value) => value !== null && value !== undefined && value.trim().length > 0

  const fields = [name, street, postalCode, city, country]

  const atLeastOneFieldSet = fields.some(isNotEmpty)

  return !atLeastOneFieldSet
}

export function validAlternativeShippingAddress(deliveryObjectValues) {
  const { name, street, postalCode, city, country } = deliveryObjectValues
  const isNotEmpty = (value) => value !== null && value !== undefined && value.trim().length > 0

  const fields = [name, street, postalCode, city, country]

  const allFieldsSet = fields.every(isNotEmpty)

  return !!allFieldsSet
}

/**
 * Takes original filters and combines them with search text
 *
 * @param {?array} filterValues
 * @param {?string} searchText
 * @param {boolean} orderTextSearch
 * @returns {array}
 */
export const getFilters = (filterValues, searchText, orderTextSearch) => {
  const hasFilter = filterValues && filterValues.length > 0

  if (!hasFilter && !searchText && !orderTextSearch) {
    return []
  }

  // exactly 7 digits
  const numberAndSkuRule = /^\d{7}$/
  let filters = hasFilter ? filterValues : []

  if (!searchText && !orderTextSearch) {
    return filters
  }

  if (searchText?.length > 0) {
    if (numberAndSkuRule.test(searchText)) {
      filters.push({ field: "orderNumber2", operator: "like", value: searchText })
    } else {
      filters.push({ field: "companyName1", operator: "like", value: searchText })
    }
  }

  if (orderTextSearch?.length > 0) {
    filters.push({ field: "customerOrderText", operator: "like", value: orderTextSearch })
  }

  return filters
}

/**
 * Merges strings with a space between
 *
 * @example
 *   mergeStrings(["Max", "Mustermann", "GmbH"])   // "Max Mustermann GmbH"
 *
 * @param {array} strings - An array of strings
 * @param {string} separator - A separator between the strings
 * @returns {string} - The merged string
 */
export const mergeStrings = (strings, separator = " ") => {
  const filteredStrings = strings?.filter((str) => str) // Removes empty or undefined strings
  const mergedString = filteredStrings?.join(separator) // Merges the strings with a space between or a passed value
  return mergedString
}

/**
 * Checks if given branches are allowed for the current case and user
 *
 * @param {string} submittedBranchId
 * @param {string} userBranchId
 * @param {boolean} isInternalCustomer
 *
 * @void
 * @throws {GraphQLError}
 */
export const checkIfBranchesAllowed = (submittedBranchId, userBranchId, isInternalCustomer) => {
  if (!submittedBranchId || submittedBranchId.length === 0) {
    throw new GraphQLError("Missing branch id!", {
      extensions: {
        code: "BAD_DATA",
      },
    })
  }

  if (CHECKOUT_DISABLED_BRANCH_IDS.includes(submittedBranchId)) {
    throw new GraphQLError(`Branch ID ${submittedBranchId} is not allowed for checkout actions!`, {
      extensions: {
        code: "NOT_ALLOWED",
      },
    })
  }

  if (isInternalCustomer && submittedBranchId !== userBranchId) {
    throw new GraphQLError("You are not allowed to submit this branch!", {
      extensions: {
        code: "FORBIDDEN",
      },
    })
  }
}

/**
 * Check if action is allowed for the given order type
 *
 * @param {string} orderType
 * @param {boolean} reservationAllowed
 * @param {boolean} isInternalCustomer
 * @param {string} admNumber
 *
 * @void
 * @throws {GraphQLError}
 */
export const checkIfActionAllowed = (orderType, reservationAllowed, isInternalCustomer, admNumber) => {
  if (!admNumber || admNumber.length === 0) {
    throw new GraphQLError("ADM_NUMBER_NOT_FOUND", {
      extensions: {
        code: "NOT_FOUND",
      },
    })
  }

  if (orderType === "reservation" && !reservationAllowed) {
    throw new GraphQLError("Reservation is not allowed for this user!", {
      extensions: {
        code: "NOT_ALLOWED",
      },
    })
  }

  const dissalowedOrderTypes = ["cashSale", "reservation"]
  if (dissalowedOrderTypes.includes(orderType) && isInternalCustomer) {
    throw new GraphQLError("Internal customers are not allowed to create entries for cash sales or reservations!", {
      extensions: {
        code: "FORBIDDEN",
      },
    })
  }
}

/**
 * Determines if the picklist toggle should be saved based on order type, shipping method, and user role
 * EINZA-1992
 *
 * @param {string} shippingMethodName - The shipping method name (e.g., "2" for shipping)
 * @param {string} orderType - The order type (e.g., "order", "cashSale", "offer", "reservation")
 * @param {boolean} isIdmUser - Whether the user is an IDM user
 * @returns {boolean} - True if picklist modal should be shown
 */
export function shouldSavePicklistToggle(shippingMethodName, orderType, isIdmUser) {
  const isShipping = shippingMethodName === "2"
  const isRelevantOrderType = orderType === "order" || orderType === "cashSale"
  return isIdmUser && isRelevantOrderType && isShipping
}
